/**
 * @module utils/membershipDetails
 * @description Detalles extra de la credencial que salen del Membership de un
 * motor externo (`membership.engine`). Los componentes compartidos llaman solo
 * a `getMembershipDetails` y renderizan las filas; no conocen el formato de
 * ningún motor. Agregar un motor = un archivo en `adapters/` + una línea acá.
 * `getValidationDetails` hace lo mismo para el banner del validador, a partir
 * del `engine` de la respuesta de `POST /validations/validate`.
 */
import dyntech, {
  validationDetails as dyntechValidation,
} from "./adapters/dyntech";

/** @type {Object<string, Function>} Adapters por `membership.engine` */
const adapters = { dyntech };

/** @type {Object<string, Function>} Adapters del validador por `engine.name` */
const validationAdapters = { dyntech: dyntechValidation };

/**
 * @description Detalles de credencial del Membership. Nunca lanza: sin
 * Membership, con motor interno/desconocido, o con un payload externo roto,
 * devuelve el resultado vacío y la credencial se ve como siempre.
 * @param {Object|null|undefined} membership - `member.membership` de `GET /members/me`
 * @returns {{memberNumber: string|null, rows: Array<{key: string, label: string, value: string}>}}
 *   `memberNumber` reemplaza al número de afiliado si viene; `rows` son
 *   filas label/valor ya formateadas
 */
export function getMembershipDetails(membership) {
  const empty = { memberNumber: null, rows: [] };
  try {
    const adapter = membership && adapters[membership.engine];
    if (!adapter) return empty;
    const details = adapter(membership);
    return {
      memberNumber: details.memberNumber || null,
      rows: Array.isArray(details.rows) ? details.rows : [],
    };
  } catch (err) {
    return empty;
  }
}

/**
 * @description Filas de detalle del afiliado para el banner del validador.
 * Nunca lanza: sin motor, motor interno/desconocido o payload roto devuelve
 * `[]` y el banner se ve como siempre.
 * @param {{name: string, details: Object}|null|undefined} engine - `engine` de
 *   la respuesta de `POST /validations/validate`
 * @returns {Array<{key: string, label: string, value: string}>}
 */
export function getValidationDetails(engine) {
  try {
    const adapter = engine && validationAdapters[engine.name];
    if (!adapter) return [];
    const rows = adapter(engine.details);
    return Array.isArray(rows) ? rows : [];
  } catch (err) {
    return [];
  }
}

export default getMembershipDetails;
